import React, { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';

export interface CookieConsent {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

interface CookieConsentContextType {
  consent: CookieConsent | null;
  showBanner: boolean;
  updateConsent: (consent: CookieConsent) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  openSettings: () => void;
  closeSettings: () => void;
  showSettings: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

const CONSENT_COOKIE_NAME = 'cookie-consent';
const CONSENT_VERSION = '1.0';

const defaultConsent: CookieConsent = {
  necessary: true, // Always true, can't be disabled
  analytics: false,
  marketing: false,
  preferences: false,
};

export function CookieConsentProvider({ children }: { children: React.ReactNode }) {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    // Check for existing consent
    const savedConsent = Cookies.get(CONSENT_COOKIE_NAME);
    
    if (savedConsent) {
      try {
        const parsed = JSON.parse(savedConsent);
        if (parsed.version === CONSENT_VERSION) {
          setConsent(parsed.consent);
          loadScripts(parsed.consent);
        } else {
          // Version mismatch, show banner after delay
          setTimeout(() => setShowBanner(true), 30000);
        }
      } catch {
        setTimeout(() => setShowBanner(true), 30000);
      }
    } else {
      // No existing consent, show banner after 30 second delay
      setTimeout(() => setShowBanner(true), 30000);
    }
  }, []);

  const updateConsent = (newConsent: CookieConsent) => {
    const consentData = {
      consent: newConsent,
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
    };

    // Save to cookie (1 year expiry)
    Cookies.set(CONSENT_COOKIE_NAME, JSON.stringify(consentData), { 
      expires: 365,
      secure: true,
      sameSite: 'strict'
    });

    setConsent(newConsent);
    setShowBanner(false);
    setShowSettings(false);
    
    // Load/unload scripts based on consent
    loadScripts(newConsent);
  };

  const acceptAll = () => {
    updateConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    });
  };

  const rejectAll = () => {
    updateConsent({
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    });
  };

  const openSettings = () => {
    setShowSettings(true);
  };

  const closeSettings = () => {
    setShowSettings(false);
  };

  const loadScripts = (consent: CookieConsent) => {
    // Load Google Analytics if analytics consent given
    if (consent.analytics && !window.gtag) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtag/js?id=G-C5DLFCWPLC';
      document.head.appendChild(script);

      script.onload = () => {
        window.dataLayer = window.dataLayer || [];
        function gtag(...args: any[]) {
          window.dataLayer.push(args);
        }
        window.gtag = gtag;
        gtag('js', new Date());
        gtag('config', 'G-C5DLFCWPLC', {
          anonymize_ip: true,
        });
      };
    }

    // Load AdSense if marketing consent given
    if (consent.marketing && !document.querySelector('script[src*="adsbygoogle"]')) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-6622383654045902';
      script.crossOrigin = 'anonymous';
      document.head.appendChild(script);
    }

    // Update gtag consent if already loaded
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: consent.analytics ? 'granted' : 'denied',
        ad_storage: consent.marketing ? 'granted' : 'denied',
        functionality_storage: consent.preferences ? 'granted' : 'denied',
        security_storage: 'granted', // Always granted for necessary cookies
      });
    }
  };

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        showBanner,
        updateConsent,
        acceptAll,
        rejectAll,
        openSettings,
        closeSettings,
        showSettings,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
}

// Extend window type for gtag
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}